import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Download, Copy, Twitter, Facebook, Share2, FileText, Square, RectangleVertical, Smartphone, RectangleHorizontal, Palette } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { toPng } from "html-to-image";
import { addWatermark } from "@/lib/watermark";
import { useAuth } from "@/contexts/AuthContext";
import { ShareableTableImage } from "./ShareableTableImage";
import BackgroundImageControls, { type BackgroundImageState } from "./share/BackgroundImageControls";
import CompositePreview from "./share/CompositePreview";
import { getVisibleRows, shortenTeamName } from "@/lib/tableUtils";
import { AUTH_DISABLED } from "@/lib/featureFlags";

// Social media aspect ratios
export const ASPECT_RATIOS = {
  '1:1': { width: 1080, height: 1080, label: 'Square (1:1)', platforms: 'Instagram, Facebook, LinkedIn' },
  '4:5': { width: 1080, height: 1350, label: 'Portrait (4:5)', platforms: 'Instagram Feed' },
  '16:9': { width: 1920, height: 1080, label: 'Landscape (16:9)', platforms: 'Twitter, LinkedIn, YouTube' },
  '9:16': { width: 1080, height: 1920, label: 'Story (9:16)', platforms: 'Instagram/Facebook Stories, TikTok' },
} as const;

export type AspectRatioKey = keyof typeof ASPECT_RATIOS;
export type OverlayAspectRatioKey = 'match' | '1:1' | '4:5' | '9:16' | '16:9';

const DEFAULT_OVERLAY_WIDTH = 800;

export const getOverlayDimensions = (
  canvasWidth: number,
  canvasHeight: number,
  overlayAspectRatio: OverlayAspectRatioKey,
  overlayWidth?: number
): { width: number; height: number } => {
  if (overlayAspectRatio === 'match') {
    return { width: canvasWidth, height: canvasHeight };
  }
  const baseWidth = overlayWidth || DEFAULT_OVERLAY_WIDTH;
  const ratioMap: Record<string, number> = {
    '1:1': 1,
    '4:5': 5 / 4,
    '9:16': 16 / 9,
    '16:9': 9 / 16,
  };
  const ratio = ratioMap[overlayAspectRatio] || 1;
  return { width: baseWidth, height: Math.round(baseWidth * ratio) };
};

export interface TableCustomizationOptions {
  customTitle?: string;
  customSubtitle?: string;
  showSeason: boolean;
  showPositionSummary: boolean;
  columns: {
    played: boolean;
    won: boolean;
    drawn: boolean;
    lost: boolean;
    gf: boolean;
    ga: boolean;
    gd: boolean;
    pts: boolean;
  };
  teamsToShow: 'auto' | 5 | 10 | 'all';
  showZoneColors: boolean;
  theme: 'default' | 'dark' | 'minimal' | 'glass' | 'transparent';
  aspectRatio: AspectRatioKey;
  overlayAspectRatio: OverlayAspectRatioKey;
  overlayWidth: number;
}

export const DEFAULT_OPTIONS: TableCustomizationOptions = {
  showSeason: true,
  showPositionSummary: true,
  columns: {
    played: true,
    won: false,
    drawn: false,
    lost: false,
    gf: false,
    ga: false,
    gd: true,
    pts: true,
  },
  teamsToShow: 'auto',
  showZoneColors: true,
  theme: 'default',
  aspectRatio: '1:1',
  overlayAspectRatio: 'match',
  overlayWidth: 800,
};

interface TableData {
  position: number;
  team: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  gd: number;
  points: number;
  isManagerTeam: boolean;
}

interface ShareableTableCustomizerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  managerName: string;
  clubName: string;
  tenurePeriod: string;
  season?: string;
  dateRange?: string;
  tableData: TableData[];
  managerId: string;
  assignmentId: string;
}

export const ShareableTableCustomizer = ({
  open,
  onOpenChange,
  managerName,
  clubName,
  tenurePeriod,
  season,
  dateRange,
  tableData,
  managerId,
  assignmentId,
}: ShareableTableCustomizerProps) => {
  const { toast } = useToast();
  const { isPremium } = useAuth();
  const navigate = useNavigate();
  const imageRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<TableCustomizationOptions>(DEFAULT_OPTIONS);
  const [bgState, setBgState] = useState<BackgroundImageState>({
    backgroundUrl: null,
    backgroundOffsetX: 0,
    backgroundOffsetY: 0,
    backgroundScale: 1,
    overlayOffsetX: 0,
    overlayOffsetY: 0,
    overlayScale: 1,
    overlayOpacity: 0.95,
    dragMode: "overlay",
  });

  const updateOption = <K extends keyof TableCustomizationOptions>(
    key: K,
    value: TableCustomizationOptions[K]
  ) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const updateColumn = (column: keyof TableCustomizationOptions['columns'], value: boolean) => {
    setOptions(prev => ({
      ...prev,
      columns: { ...prev.columns, [column]: value },
    }));
  };

  const applyPreset = (preset: 'compact' | 'standard' | 'detailed') => {
    const presets = {
      compact: { played: true, won: false, drawn: false, lost: false, gf: false, ga: false, gd: true, pts: true },
      standard: { played: true, won: true, drawn: true, lost: true, gf: false, ga: false, gd: true, pts: true },
      detailed: { played: true, won: true, drawn: true, lost: true, gf: true, ga: true, gd: true, pts: true },
    };
    setOptions(prev => ({ ...prev, columns: presets[preset] }));
  };

  const generateImage = async (): Promise<Blob | null> => {
    if (!imageRef.current) {
      toast({
        title: "Error",
        description: "Unable to generate image. Please try again.",
        variant: "destructive",
      });
      return null;
    }

    try {
      let dataUrl = await toPng(imageRef.current, {
        quality: 1,
        pixelRatio: 2,
      });
      dataUrl = await addWatermark(dataUrl, isPremium);
      const response = await fetch(dataUrl);
      return await response.blob();
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Error",
        description: "Failed to generate image. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleDownload = async () => {
    const blob = await generateImage();
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${managerName.replace(/\s+/g, "-")}-${season || "table"}-${options.aspectRatio.replace(':', 'x')}.png`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: "Downloaded!", description: "Image saved successfully." });
  };

  const handleCopyToClipboard = async () => {
    const blob = await generateImage();
    if (!blob) return;

    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast({ title: "Copied!", description: "Image copied to clipboard." });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy image to clipboard.",
        variant: "destructive",
      });
    }
  };

  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams();
    params.append('manager', managerId);
    params.append('assignment', assignmentId);
    if (season) params.append('season', season);
    return `${baseUrl}?${params.toString()}`;
  };

  const handleShareTwitter = () => {
    const text = `Check out ${managerName}'s league table${season ? ` for ${season}` : ""}! #Football #ManagerTable`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(generateShareUrl())}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleShareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(generateShareUrl())}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast({
        title: "Not supported",
        description: "Your browser doesn't support native sharing.",
        variant: "destructive",
      });
      return;
    }

    const blob = await generateImage();
    if (!blob) return;

    const file = new File([blob], `${managerName.replace(/\s+/g, "-")}-table.png`, { type: "image/png" });

    try {
      await navigator.share({
        title: `${managerName} - League Table`,
        text: `Check out ${managerName}'s league table${season ? ` for ${season}` : ""}!`,
        files: [file],
        url: generateShareUrl(),
      });
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        toast({
          title: "Error",
          description: "Failed to share image.",
          variant: "destructive",
        });
      }
    }
  };

  const generateTextTable = (): string => {
    const managerRow = tableData.find(row => row.isManagerTeam);
    const managerPosition = managerRow?.position ?? null;
    const totalTeams = tableData.length;
    
    // Get visible rows using same logic as image
    const { rows: visibleRows, showTopEllipsis, showBottomEllipsis } = getVisibleRows(
      tableData, 
      totalTeams, 
      options.teamsToShow
    );
    
    const lines: string[] = [];
    
    // Compact header with shortened club name
    const shortClub = shortenTeamName(clubName);
    lines.push(`⚽ ${options.customTitle || managerName} - ${shortClub}`);
    
    // Position & season on one compact line
    const metaParts: string[] = [];
    if (options.showPositionSummary && managerPosition) {
      metaParts.push(`#${managerPosition} of ${totalTeams}`);
    }
    if (options.showSeason && season) {
      metaParts.push(season);
    }
    if (metaParts.length > 0) {
      lines.push(`📊 ${metaParts.join(' | ')}`);
    }
    lines.push('');
    
    // Table rows - clean proportional-font-friendly format
    if (showTopEllipsis) lines.push('···');
    
    for (const row of visibleRows) {
      const shortName = shortenTeamName(row.team);
      const gd = row.gd > 0 ? `+${row.gd}` : String(row.gd);
      const marker = row.isManagerTeam ? ' ◀' : '';
      
      // Build stats based on enabled columns
      const statParts: string[] = [];
      if (options.columns.pts) statParts.push(`${row.points}pts`);
      if (options.columns.gd) statParts.push(`(${gd})`);
      if (options.columns.played && !options.columns.pts) statParts.push(`${row.played}P`);
      if (options.columns.won) statParts.push(`${row.won}W`);
      if (options.columns.drawn) statParts.push(`${row.drawn}D`);
      if (options.columns.lost) statParts.push(`${row.lost}L`);
      
      const statsStr = statParts.length > 0 ? ` — ${statParts.join(' ')}` : '';
      const posStr = row.position.toString().padStart(2, ' ');
      
      lines.push(`${posStr}. ${shortName}${statsStr}${marker}`);
    }
    
    if (showBottomEllipsis) lines.push('···');
    
    return lines.join('\n');
  };
  
  // Character count for social media limits
  const textOutput = generateTextTable();
  const charCount = textOutput.length;

  const handleCopyText = async () => {
    const text = generateTextTable();
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Text table copied to clipboard." });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy text to clipboard.",
        variant: "destructive",
      });
    }
  };

  const currentRatio = ASPECT_RATIOS[options.aspectRatio];
  const previewScale = Math.min(320 / currentRatio.width, 400 / currentRatio.height);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customize & Share Image</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Controls */}
          <div className="space-y-6">
            {/* Canvas Aspect Ratio - always visible */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Canvas Aspect Ratio</Label>
              <ToggleGroup
                type="single"
                value={options.aspectRatio}
                onValueChange={(v) => v && updateOption('aspectRatio', v as AspectRatioKey)}
                className="grid grid-cols-4 gap-1"
              >
                <ToggleGroupItem value="1:1" className="text-xs px-2">
                  <Square className="h-3 w-3 mr-1" />1:1
                </ToggleGroupItem>
                <ToggleGroupItem value="4:5" className="text-xs px-2">
                  <RectangleVertical className="h-3 w-3 mr-1" />4:5
                </ToggleGroupItem>
                <ToggleGroupItem value="9:16" className="text-xs px-2">
                  <Smartphone className="h-3 w-3 mr-1" />9:16
                </ToggleGroupItem>
                <ToggleGroupItem value="16:9" className="text-xs px-2">
                  <RectangleHorizontal className="h-3 w-3 mr-1" />16:9
                </ToggleGroupItem>
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">{ASPECT_RATIOS[options.aspectRatio].platforms}</p>
            </div>

            {/* Overlay Size - always visible */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Overlay Size</Label>
              <ToggleGroup
                type="single"
                value={options.overlayAspectRatio}
                onValueChange={(v) => v && updateOption('overlayAspectRatio', v as OverlayAspectRatioKey)}
                className="grid grid-cols-5 gap-1"
              >
                <ToggleGroupItem value="match" className="text-xs px-2">Fill</ToggleGroupItem>
                <ToggleGroupItem value="1:1" className="text-xs px-2">
                  <Square className="h-3 w-3 mr-1" />1:1
                </ToggleGroupItem>
                <ToggleGroupItem value="4:5" className="text-xs px-2">
                  <RectangleVertical className="h-3 w-3 mr-1" />4:5
                </ToggleGroupItem>
                <ToggleGroupItem value="9:16" className="text-xs px-2">
                  <Smartphone className="h-3 w-3 mr-1" />9:16
                </ToggleGroupItem>
                <ToggleGroupItem value="16:9" className="text-xs px-2">
                  <RectangleHorizontal className="h-3 w-3 mr-1" />16:9
                </ToggleGroupItem>
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">Controls overlay card size independently of the export canvas</p>
              {options.overlayAspectRatio !== 'match' && (
                <div className="space-y-1.5 pt-2">
                  <Label className="text-xs font-medium text-muted-foreground">
                    Overlay Width: {options.overlayWidth}px
                  </Label>
                  <Slider
                    value={[options.overlayWidth]}
                    onValueChange={([v]) => updateOption('overlayWidth', v)}
                    min={400}
                    max={1200}
                    step={20}
                  />
                </div>
              )}
            </div>

            <Tabs defaultValue="header" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="header">Header</TabsTrigger>
                <TabsTrigger value="columns">Columns</TabsTrigger>
                <TabsTrigger value="display">Display</TabsTrigger>
                <TabsTrigger value="theme">Theme</TabsTrigger>
                <TabsTrigger value="background">Background</TabsTrigger>
              </TabsList>

              <TabsContent value="header" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="customTitle">Custom Title</Label>
                  <Input
                    id="customTitle"
                    placeholder={managerName}
                    value={options.customTitle || ''}
                    onChange={(e) => updateOption('customTitle', e.target.value || undefined)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customSubtitle">Custom Subtitle</Label>
                  <Input
                    id="customSubtitle"
                    placeholder="Add a tagline..."
                    value={options.customSubtitle || ''}
                    onChange={(e) => updateOption('customSubtitle', e.target.value || undefined)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="showSeason">Show Season</Label>
                  <Switch
                    id="showSeason"
                    checked={options.showSeason}
                    onCheckedChange={(checked) => updateOption('showSeason', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="showPositionSummary">Show Position Summary</Label>
                  <Switch
                    id="showPositionSummary"
                    checked={options.showPositionSummary}
                    onCheckedChange={(checked) => updateOption('showPositionSummary', checked)}
                  />
                </div>
              </TabsContent>

              <TabsContent value="columns" className="space-y-4 mt-4">
                <div className="flex gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={() => applyPreset('compact')}>
                    Compact
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => applyPreset('standard')}>
                    Standard
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => applyPreset('detailed')}>
                    Detailed
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(options.columns).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={key}
                        checked={value}
                        onCheckedChange={(checked) => 
                          updateColumn(key as keyof TableCustomizationOptions['columns'], !!checked)
                        }
                      />
                      <Label htmlFor={key} className="text-sm uppercase">{key}</Label>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="display" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Teams to Show</Label>
                  <Select
                    value={String(options.teamsToShow)}
                    onValueChange={(value) => 
                      updateOption('teamsToShow', value === 'auto' || value === 'all' ? value : Number(value) as 5 | 10)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (Center on Manager)</SelectItem>
                      <SelectItem value="5">Top 5</SelectItem>
                      <SelectItem value="10">Top 10</SelectItem>
                      <SelectItem value="all">All Teams</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="theme" className="space-y-4 mt-4">
                <RadioGroup
                  value={options.theme}
                  onValueChange={(value) => updateOption('theme', value as TableCustomizationOptions['theme'])}
                  className="grid grid-cols-3 gap-4"
                >
                  <div>
                    <RadioGroupItem value="default" id="theme-default" className="peer sr-only" />
                    <Label
                      htmlFor="theme-default"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-gradient-to-br from-indigo-900 to-blue-800 p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <span className="text-white text-sm font-medium">Default</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="dark" id="theme-dark" className="peer sr-only" />
                    <Label
                      htmlFor="theme-dark"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-gradient-to-br from-slate-900 to-slate-800 p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <span className="text-white text-sm font-medium">Dark</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="minimal" id="theme-minimal" className="peer sr-only" />
                    <Label
                      htmlFor="theme-minimal"
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-white p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    >
                      <span className="text-slate-900 text-sm font-medium">Minimal</span>
                    </Label>
                  </div>
                </RadioGroup>
              </TabsContent>

              <TabsContent value="background" className="space-y-4 mt-4">
                <BackgroundImageControls state={bgState} onChange={setBgState} />
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              <Button onClick={handleDownload} className="flex-1">
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
              <Button onClick={handleCopyToClipboard} variant="outline" className="flex-1">
                <Copy className="mr-2 h-4 w-4" />
                Copy Image
              </Button>
              <Button onClick={handleCopyText} variant="outline" className="flex-1">
                <FileText className="mr-2 h-4 w-4" />
                Copy Text
              </Button>
            </div>
            {!AUTH_DISABLED && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                navigate('/create?manager=' + managerId + '&type=manager-table', {
                  state: {
                    tableTransfer: {
                      tableData,
                      options,
                      managerName,
                      clubName,
                      tenurePeriod,
                      season,
                      dateRange,
                      managerId,
                      assignmentId,
                    },
                  },
                });
              }}
            >
              <Palette className="mr-2 h-4 w-4" />
              Edit in Creator
            </Button>
            )}
            <div className="text-sm text-muted-foreground">
              {charCount}/280 characters
              {charCount > 280 && <span className="text-destructive ml-1">(exceeds Twitter limit)</span>}
              {charCount <= 280 && charCount > 240 && <span className="text-amber-500 ml-1">(near limit)</span>}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleShareTwitter} variant="outline" size="icon" aria-label="Share on X">
                <Twitter className="h-4 w-4" />
              </Button>
              <Button onClick={handleShareFacebook} variant="outline" size="icon" aria-label="Share on Facebook">
                <Facebook className="h-4 w-4" />
              </Button>
              {navigator.share && (
                <Button onClick={handleNativeShare} variant="outline" size="icon" aria-label="Share">
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex flex-col items-center">
            <Label className="mb-2 text-sm text-muted-foreground">
              Preview ({currentRatio.width}×{currentRatio.height})
            </Label>
            <div
              className="border rounded-lg overflow-hidden bg-muted/30"
              style={{
                width: currentRatio.width * previewScale,
                height: currentRatio.height * previewScale,
              }}
            >
              <div
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  width: currentRatio.width,
                  height: currentRatio.height,
                }}
              >
                <CompositePreview
                  ref={imageRef}
                  backgroundState={bgState}
                  width={currentRatio.width}
                  height={currentRatio.height}
                  onStateChange={setBgState}
                >
                  <ShareableTableImage
                    managerName={options.customTitle || managerName}
                    clubName={clubName}
                    tenurePeriod={tenurePeriod}
                    season={options.showSeason ? season : undefined}
                    dateRange={dateRange}
                    tableData={tableData}
                    options={options}
                  />
                </CompositePreview>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
